import type {
  CitationSource,
  CitedDocumentSource,
  CitedSource,
} from "@/shared/lib/citations";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS, type NuggetSeverity } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import {
  isReaction,
  nextFeedbackAfterReactionClick,
  type FeedbackState,
  type Reaction,
} from "@/shared/lib/feedbackReaction";
import {
  addNuggetFeedbackComment as requestNuggetFeedbackComment,
  setNuggetReaction as requestNuggetReaction,
} from "@/features/nuggets/lib/nuggetFeedbackApi";
import { isSafeSourceUrl } from "@/features/nuggets/model/nuggetCitations";
import { setNuggetSeverity as requestNuggetSeverity } from "@/features/nuggets/lib/nuggetSeverityApi";
import type { NuggetProvenance, NuggetQualityReview } from "@/features/nuggets/model/nuggetDetails";

// Re-exported so existing imports from this module (`nextFeedbackAfterReactionClick`,
// `NuggetReaction`, `NuggetFeedback`) keep working unchanged — the implementation now lives in
// shared/lib/feedbackReaction.ts, shared with chat turn feedback.
export { nextFeedbackAfterReactionClick };

export type NuggetBriefItem = {
  title: string;
  description: string;
};

export type SuggestedApp = {
  id: number;
  name: string;
};

/** One external source the authored text cites. `id` is the number that appears inline in any
 * nugget field as `[1]` — the citation is not tied to a particular section.
 *
 * An alias of the shared `CitedSource` since chat's action research started carrying the same
 * shape: one renderer resolves both, so one type describes both. */
export type NuggetSource = CitedSource;

/** A page of one of the domain's own uploaded documents. Shared with chat's renderer — see
 * `CitedDocumentSource`. */
export type NuggetDocumentSource = CitedDocumentSource;

/** Anything a `[1]` marker in authored text can resolve to: a web page or a document page.
 *
 * Kept as a separate name from `NuggetSource` rather than replacing it, so that `details.sources`
 * (which only ever holds web sources) keeps its precise type and every existing caller reading
 * `.url` off it still type-checks. Only the citation renderers widen to this. */
export type NuggetCitationSource = CitationSource;

/** One statement read out of a document, as the card persists it. Supporting context beside the
 * card's own measured numbers — never a claim about them, and never a cause. */
export type NuggetDocumentStatement = {
  statement: string;
  placement?: "insight" | "why";
  figure?: string | null;
  stated_source?: string | null;
  citations?: { source_file: string; page?: number | null }[];
};

/** What the domain's documents contributed to this card. Absent on most cards. */
export type NuggetDocumentContext = {
  statements?: NuggetDocumentStatement[];
  source_refs?: {
    /** The number the card's own prose cites this page by. Authored by the pipeline — never
     * renumbered here, or a `[2]` in a sentence would point at a different page. */
    id: number;
    source_file: string;
    page?: number | null;
    doc_title?: string | null;
    timeline?: string | null;
    is_image?: boolean;
    preview_image?: string | null;
  }[];
  passage_query?: string;
  passages_retrieved?: number;
  passages_accepted?: number;
};

export type IntegrationRecommendedAction = {
  kind: "INTEGRATION";
  integration_id: number;
  integration_type: string;
  action_type: string;
  message: string;
  image_prompt?: string;
};

export type ManualRecommendedAction = {
  kind: "MANUAL";
  message: string;
};

export type RecommendedAction = IntegrationRecommendedAction | ManualRecommendedAction;

export type NuggetDetails = {
  title: string;
  summary: string;
  severity?: string;
  lens?: string;
  insights?: NuggetBriefItem[];
  why?: NuggetBriefItem[];
  action?: NuggetBriefItem[];
  recommended_actions?: RecommendedAction[];
  business_impact?: NuggetBriefItem[];
  how_generated?: NuggetBriefItem[];
  provenance?: NuggetProvenance;
  quality_review?: NuggetQualityReview;
  focus_area?: string;
  timeframe_summary?: string;
  follow_up_questions?: string[];
  suggested_apps?: SuggestedApp[];
  /** Referenced by the `[1]`-style markers inside any authored field. */
  sources?: NuggetSource[];
  /** Supporting context the domain's own documents added to this card. The source list the
   * modal shows is built from this alongside `sources`, so a document citation resolves the
   * same way a web one does. */
  document_context?: NuggetDocumentContext;
};

export type NuggetReaction = Reaction;

/** The requesting user's own feedback on a nugget. `reaction` is null when they have none. */
export type NuggetFeedback = FeedbackState;

export type Nugget = {
  id: number;
  domain_id: number;
  details: NuggetDetails;
  created_at: string;
  updated_at: string;
  feedback?: NuggetFeedback | null;
};

export type NuggetsState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  nuggets: Nugget[];
};

export const nuggetsInitialState: NuggetsState = {
  status: STATUS.IDLE,
  error: null,
  nuggets: [],
};

function isNuggetBriefItem(value: unknown): value is NuggetBriefItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const item = value as Record<string, unknown>;
  return typeof item.title === "string" && typeof item.description === "string";
}

/** Accepts the current NuggetBriefItem[] shape as well as the legacy plain-string shape. */
function isValidNuggetBriefField(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    typeof value === "string" ||
    (Array.isArray(value) && value.every(isNuggetBriefItem))
  );
}

function isValidFollowUpQuestions(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.every((q) => typeof q === "string"))
  );
}

function isValidSuggestedApps(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (Array.isArray(value) &&
      value.every(
        (app) =>
          typeof app === "object" &&
          app !== null &&
          typeof (app as Record<string, unknown>).id === "number" &&
          typeof (app as Record<string, unknown>).name === "string",
      ))
  );
}

/** Deliberately shallow — `normalizeNuggetSources` drops the entries it cannot use. A single
 * malformed source must not fail `isNugget` and hide the whole insight from the feed. */
function isValidSources(value: unknown): boolean {
  return value === undefined || value === null || Array.isArray(value);
}

function isIntegrationRecommendedAction(
  item: Record<string, unknown>,
): item is IntegrationRecommendedAction {
  return (
    item.kind === "INTEGRATION" &&
    typeof item.integration_id === "number" &&
    typeof item.integration_type === "string" &&
    typeof item.action_type === "string" &&
    typeof item.message === "string"
  );
}

function isManualRecommendedAction(
  item: Record<string, unknown>,
): item is ManualRecommendedAction {
  return item.kind === "MANUAL" && typeof item.message === "string";
}

function isRecommendedAction(value: unknown): value is RecommendedAction {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const item = value as Record<string, unknown>;
  return isIntegrationRecommendedAction(item) || isManualRecommendedAction(item);
}

function isValidRecommendedActions(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.every(isRecommendedAction))
  );
}

const BRIEF_ITEM_FIELDS = ["insights", "why", "action", "business_impact", "how_generated"] as const;

/**
 * The first reason `isNuggetDetails` would reject `value`, or null when it accepts it.
 *
 * The manual-upload dialog shows this verbatim. "Invalid nugget details" on its own leaves
 * whoever pasted the JSON re-reading a few hundred lines with no idea which field is wrong --
 * and the answer is nearly always one specific key.
 */
export function nuggetDetailsProblem(value: unknown): string | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return "Details must be a JSON object, not an array or primitive.";
  }
  const row = value as Record<string, unknown>;
  if (typeof row.title !== "string") {
    return '"title" is required and must be a string.';
  }
  if (typeof row.summary !== "string") {
    return '"summary" is required and must be a string.';
  }
  for (const field of BRIEF_ITEM_FIELDS) {
    if (!isValidNuggetBriefField(row[field])) {
      return `"${field}" must be a list of { "title", "description" } objects.`;
    }
  }
  if (!isValidFollowUpQuestions(row.follow_up_questions)) {
    return '"follow_up_questions" must be a list of strings.';
  }
  if (!isValidSuggestedApps(row.suggested_apps)) {
    return '"suggested_apps" must be a list of { "id", "name" } objects.';
  }
  if (!isValidSources(row.sources)) {
    return '"sources" must be a list of { "id", "url" } objects.';
  }
  if (!isValidRecommendedActions(row.recommended_actions)) {
    return '"recommended_actions" must be a list of MANUAL or INTEGRATION actions.';
  }
  return null;
}

/** Kept as the predicate every caller already uses; the reasons live in one place above. */
export function isNuggetDetails(value: unknown): value is NuggetDetails {
  return nuggetDetailsProblem(value) === null;
}

/** Converts a legacy plain-string field into the current NuggetBriefItem[] shape. */
function normalizeNuggetBriefField(value: unknown): NuggetBriefItem[] | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? [{ title: "", description: trimmed }] : [];
  }
  return value as NuggetBriefItem[];
}

function optionalSourceText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
}

/** Accepts the number the markers use, and the `"1"` / `"[1]"` string forms an authoring model
 * sometimes emits instead. */
function toSourceId(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isInteger(value) && value > 0 ? value : null;
  }
  if (typeof value === "string") {
    const digits = value.match(/\d+/);
    const parsed = digits ? Number(digits[0]) : 0;
    return parsed > 0 ? parsed : null;
  }
  return null;
}

/** Keeps only sources that can actually be linked, and numbers the rest by position — an
 * unnumbered list is still `[1]`, `[2]`, `[3]` to the reader. */
function normalizeNuggetSources(value: unknown): NuggetSource[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const sources: NuggetSource[] = [];
  value.forEach((entry, entryIndex) => {
    if (typeof entry !== "object" || entry === null) {
      return;
    }
    const row = entry as Record<string, unknown>;
    const url = typeof row.url === "string" ? row.url.trim() : "";
    if (!isSafeSourceUrl(url)) {
      return;
    }
    sources.push({
      id: toSourceId(row.id) ?? entryIndex + 1,
      url,
      title: optionalSourceText(row.title),
      publisher: optionalSourceText(row.publisher),
      published_date: optionalSourceText(row.published_date),
      accessed_date: optionalSourceText(row.accessed_date),
    });
  });
  return sources;
}

function normalizeNuggetDetails(details: NuggetDetails): NuggetDetails {
  return {
    ...details,
    insights: normalizeNuggetBriefField(details.insights),
    why: normalizeNuggetBriefField(details.why),
    action: normalizeNuggetBriefField(details.action),
    business_impact: normalizeNuggetBriefField(details.business_impact),
    how_generated: normalizeNuggetBriefField(details.how_generated),
    sources: normalizeNuggetSources(details.sources),
  };
}

export function normalizeNugget(nugget: Nugget): Nugget {
  return { ...nugget, details: normalizeNuggetDetails(nugget.details) };
}

/** Tolerates a missing/null `feedback` so nuggets still parse against a backend that
 * predates the feedback field — `parseNuggetsResponse` silently drops rows that fail. */
function isValidNuggetFeedback(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value !== "object") {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    (row.reaction === null || isReaction(row.reaction)) &&
    (row.comment === null || row.comment === undefined || typeof row.comment === "string")
  );
}

export function isNugget(value: unknown): value is Nugget {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.domain_id === "number" &&
    isNuggetDetails(row.details) &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string" &&
    isValidNuggetFeedback(row.feedback)
  );
}

function parseNuggetsResponse(data: unknown): Nugget[] | null {
  if (!Array.isArray(data)) {
    return null;
  }
  return data.filter(isNugget).map(normalizeNugget);
}

export const deleteNugget = createAsyncThunk<number, number, { rejectValue: string }>(
  "nuggets/delete",
  async (nuggetId, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/nuggets/${nuggetId}`, { method: "DELETE" });
      if (!res.ok) {
        return rejectWithValue(`Request failed (${res.status})`);
      }
      return nuggetId;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export const fetchNuggets = createAsyncThunk<Nugget[], void, { rejectValue: string }>(
  "nuggets/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiFetch("/v1/nuggets", { method: "GET" });
      if (!res.ok) {
        return rejectWithValue(`Request failed (${res.status})`);
      }
      const data: unknown = await res.json();
      const nuggets = parseNuggetsResponse(data);
      if (!nuggets) {
        return rejectWithValue("Unexpected response shape");
      }
      return nuggets;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      return rejectWithValue(message);
    }
  },
);

export type ManualNuggetDetails = Record<string, unknown>;

export const manualInsertNugget = createAsyncThunk<
  Nugget,
  { domainId: number; details: ManualNuggetDetails },
  { rejectValue: string }
>("nuggets/manualInsert", async ({ domainId, details }, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/nuggets", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ domain_id: domainId, details }),
    });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    if (!isNugget(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return normalizeNugget(data);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Failed to insert nugget");
  }
});

/** Body for `PATCH /v1/nuggets/{id}` covering the detail modal's editable narrative fields.
 * Severity has its own dedicated thunk/control (`setNuggetSeverity`) and is not included here. */
export type NuggetDetailsUpdatePayload = Partial<{
  title: string;
  summary: string;
  lens: string;
  focus_area: string;
  timeframe_summary: string;
  insights: NuggetBriefItem[];
  why: NuggetBriefItem[];
  business_impact: NuggetBriefItem[];
  how_generated: NuggetBriefItem[];
  follow_up_questions: string[];
}>;

export const updateNuggetDetails = createAsyncThunk<
  Nugget,
  { nuggetId: number; updates: NuggetDetailsUpdatePayload },
  { rejectValue: string }
>("nuggets/updateDetails", async ({ nuggetId, updates }, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`/v1/nuggets/${nuggetId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    });
    if (!res.ok) {
      return rejectWithValue(`Request failed (${res.status})`);
    }
    const data: unknown = await res.json();
    if (!isNugget(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return normalizeNugget(data);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Failed to save nugget");
  }
});

type NuggetFeedbackResult = { nuggetId: number; feedback: NuggetFeedback };

type SetReactionArg = {
  nuggetId: number;
  reaction: NuggetReaction;
  /** Restored verbatim if the request fails — the reducers cannot capture it themselves. */
  previousFeedback: NuggetFeedback | null;
};

export const setNuggetReaction = createAsyncThunk<
  NuggetFeedbackResult,
  SetReactionArg,
  { rejectValue: string }
>("nuggets/setReaction", async ({ nuggetId, reaction }, { rejectWithValue }) => {
  try {
    const feedback = await requestNuggetReaction(nuggetId, reaction);
    return { nuggetId, feedback };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Failed to save reaction");
  }
});

export const submitNuggetFeedbackComment = createAsyncThunk<
  NuggetFeedbackResult,
  { nuggetId: number; comment: string },
  { rejectValue: string }
>("nuggets/submitFeedbackComment", async ({ nuggetId, comment }, { rejectWithValue }) => {
  try {
    const feedback = await requestNuggetFeedbackComment(nuggetId, comment);
    return { nuggetId, feedback };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Failed to save comment");
  }
});

function patchNuggetFeedback(
  state: NuggetsState,
  nuggetId: number,
  feedback: NuggetFeedback | null,
): void {
  const target = state.nuggets.find((nugget) => nugget.id === nuggetId);
  if (target) {
    target.feedback = feedback;
  }
}

type NuggetSeverityResult = { nuggetId: number; severity: NuggetSeverity | null };

type SetSeverityArg = {
  nuggetId: number;
  severity: NuggetSeverity;
  /** Restored verbatim if the request fails — the reducers cannot capture it themselves. */
  previousSeverity: NuggetSeverity | null;
};

export const setNuggetSeverity = createAsyncThunk<
  NuggetSeverityResult,
  SetSeverityArg,
  { rejectValue: string }
>("nuggets/setSeverity", async ({ nuggetId, severity }, { rejectWithValue }) => {
  try {
    const saved = await requestNuggetSeverity(nuggetId, severity);
    return { nuggetId, severity: saved };
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Failed to save severity");
  }
});

function patchNuggetSeverity(
  state: NuggetsState,
  nuggetId: number,
  severity: NuggetSeverity | null,
): void {
  const target = state.nuggets.find((nugget) => nugget.id === nuggetId);
  if (!target) {
    return;
  }
  if (severity === null) {
    // Rolling back to never-set. Assigning undefined would leave a dangling key that renders the
    // same but breaks deep-equality against a freshly fetched row.
    delete target.details.severity;
    return;
  }
  target.details.severity = severity;
}

const nuggetsSlice = createSlice({
  name: "nuggets",
  initialState: nuggetsInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchNuggets.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchNuggets.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.nuggets = action.payload;
      })
      .addCase(fetchNuggets.rejected, (state) => {
        state.status = STATUS.SUCCEEDED;
        state.nuggets = [];
        state.error = null;
      })
      .addCase(deleteNugget.fulfilled, (state, action) => {
        state.nuggets = state.nuggets.filter((nugget) => nugget.id !== action.payload);
      })
      .addCase(manualInsertNugget.fulfilled, (state, action) => {
        state.nuggets = [action.payload, ...state.nuggets];
      })
      // A Save-button flow with its own dialog-local `isSaving`/error state, not an instant-apply
      // control -- no optimistic pending patch needed, just reconcile with the saved row.
      .addCase(updateNuggetDetails.fulfilled, (state, action) => {
        const index = state.nuggets.findIndex((nugget) => nugget.id === action.payload.id);
        if (index !== -1) {
          state.nuggets[index] = action.payload;
        }
      })
      // Paint the thumb on click, then reconcile with whatever the server actually stored.
      .addCase(setNuggetReaction.pending, (state, action) => {
        const { nuggetId, reaction, previousFeedback } = action.meta.arg;
        patchNuggetFeedback(
          state,
          nuggetId,
          nextFeedbackAfterReactionClick(previousFeedback, reaction),
        );
      })
      .addCase(setNuggetReaction.fulfilled, (state, action) => {
        patchNuggetFeedback(state, action.payload.nuggetId, action.payload.feedback);
      })
      .addCase(setNuggetReaction.rejected, (state, action) => {
        patchNuggetFeedback(state, action.meta.arg.nuggetId, action.meta.arg.previousFeedback);
      })
      .addCase(submitNuggetFeedbackComment.fulfilled, (state, action) => {
        patchNuggetFeedback(state, action.payload.nuggetId, action.payload.feedback);
      })
      // The dropdown is controlled from this store, so an await-then-update would visibly snap the
      // selection back to the old level until the response lands. Paint on change, reconcile with
      // whatever the server stored, restore on failure. Severity only — never a wholesale row
      // replace, which could clobber locally-known feedback.
      .addCase(setNuggetSeverity.pending, (state, action) => {
        patchNuggetSeverity(state, action.meta.arg.nuggetId, action.meta.arg.severity);
      })
      .addCase(setNuggetSeverity.fulfilled, (state, action) => {
        patchNuggetSeverity(state, action.payload.nuggetId, action.payload.severity);
      })
      .addCase(setNuggetSeverity.rejected, (state, action) => {
        patchNuggetSeverity(state, action.meta.arg.nuggetId, action.meta.arg.previousSeverity);
      });
  },
});

export const nuggetsReducer = nuggetsSlice.reducer;
